import clsx from 'clsx';
import * as React from 'react';
import { useCallback } from 'react';
import { createId } from '../../../../helpers/typeUtils';
import { AppRadioGroupInputProps } from './appRadioGroupInput';
import styles from './appRadioGroupInput.module.scss';
import { AppRadioInput } from './appRadioInput.component';

export function AppRadioGroupInput<TOption, TValue extends string | number = string | number>(
  props: AppRadioGroupInputProps<TOption, TValue>,
) {
  const {
    rootClassName,
    options,
    valueField,
    labelField,
    iconField,
    disabled,
    onChange,
    isVertical,
    radioItemClassName,
    style,
  } = props;
  const getLabelForOption = useCallback((option: TOption) => option[labelField], [labelField]);
  const getIconForOption = useCallback((option: TOption) => (iconField && option[iconField]) || undefined, [iconField]);
  const groupName = createId();

  const getValueForOption = useCallback((option: TOption) => option[valueField] as TValue, [valueField]);

  const onOptionChanged = useCallback(
    (selectedOption: TOption) => (e: React.FormEvent<HTMLInputElement>) => {
      if (e.currentTarget.value !== props.value) {
        onChange(selectedOption);
      }
    },
    [onChange, props.value],
  );

  return (
    <div
      style={style}
      className={clsx(styles.rootContainer, { [styles.verticalContainer]: isVertical, rootClassName })}
    >
      {options.map((option, i, arr) => {
        const value = getValueForOption(option);
        const labelLocal = getLabelForOption(option)?.toString();
        const getIndex = () => {
          switch (props.numerationOrder) {
            case 'Ltr':
              return (props.numerationStartIndex ?? 0) + i;
            case 'Rtl':
              return (props.numerationStartIndex ?? 0) + arr.length - 1 - i;
            default:
            case 'NoNum':
              return undefined;
          }
        };
        const icon = getIconForOption(option) as JSX.Element | undefined;
        const prefix =
          getIndex() !== undefined || icon ? (
            <div className={styles.prefix}>
              {getIndex() !== undefined ? `(${getIndex()})` : undefined}
              {icon}
            </div>
          ) : undefined;

        return (
          <div
            key={i}
            className={clsx(
              styles.fieldContainer,
              {
                [styles.fieldContainerVertical]: isVertical,
              },
              radioItemClassName,
            )}
          >
            <AppRadioInput
              key={value}
              checked={props.value === value}
              prefixComponent={prefix}
              name={groupName}
              caption={labelLocal}
              disabled={disabled}
              onChange={onOptionChanged(option)}
            />
          </div>
        );
      })}
    </div>
  );
}
